import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PublicHeader } from "@/components/layout/PublicHeader";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScoreRing } from "@/components/ScoreRing";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { PricingUrgencyBanner, PlanPriceMeta } from "@/components/PricingUrgency";
import {
  Mic, ClipboardCheck, BookOpen, Users, Camera, ShieldCheck,
  ArrowRight, Check, Stethoscope, Star, Sparkles, Video, FileText, Radio,
} from "lucide-react";

const FEATURES = [
  { icon: Users, title: "Live peer role-play", desc: "Practise face-to-face over video with other AMC candidates — get matched instantly, or host your own private room for up to 3.", tint: "#E11D48", soft: "#FFE4E6" },
  { icon: Mic, title: "AI patient role-play", beta: true, desc: "Talk or type with a realistic simulated patient that reacts to your empathy and questioning, just like exam day.", tint: "#0F766E", soft: "#D7F3EF" },
  { icon: ClipboardCheck, title: "AI examiner feedback", desc: "Scored 1–7 on the domains each station actually assesses, with a holistic overall score, a clear pass/fail call, and specific strengths & improvements.", tint: "#0EA5E9", soft: "#D9F0FF" },
  { icon: Camera, title: "Your own custom cases", desc: "Snap a photo of your study notes or paste text — our AI turns it into a private, practiseable scenario.", tint: "#16A34A", soft: "#DCFCE7" },
  { icon: BookOpen, title: "Curated case library", desc: "Practise real AMC-style cases across Medicine, Surgery, Paediatrics, Psychiatry, O&G and Emergency.", tint: "#B45309", soft: "#FDE7C7" },
  { icon: ShieldCheck, title: "Private & secure", desc: "Your custom cases and transcripts stay private to you. Secure payments via Stripe.", tint: "#334155", soft: "#E2E8F0" },
];

const GLANCE = [
  { icon: Mic, title: "AI patient & examiner", desc: "Voice role-play that talks back — dual roles for standard, psych & counselling stations." },
  { icon: ClipboardCheck, title: "1–7 AMC feedback", desc: "Scored on the domains each station assesses, with a holistic global rating and a clear pass/fail call." },
  { icon: Users, title: "Live peer video", desc: "Get matched instantly or host a private room and swap doctor/patient roles." },
  { icon: BookOpen, title: "165+ cases", desc: "Recall-based stations across every specialty — history, exam, diagnosis & counselling." },
  { icon: Camera, title: "Your own cases", desc: "Turn study notes or a photo into a private, practiseable scenario in seconds." },
  { icon: FileText, title: "Model answers + PDF", desc: "Save model answers to your revision list and export a study PDF for offline revision." },
];

const STATS = [
  { value: "165+", label: "Practice cases" },
  { value: "10", label: "Specialties" },
  { value: "1–7", label: "AMC marking" },
  { value: "24/7", label: "Practise anytime" },
];

const STEPS = [
  { n: "01", icon: BookOpen, title: "Pick or create a case", desc: "Choose from 165+ AMC scenarios across 10 specialties, or turn your own notes and photos into a private practice case.", tint: "#0F766E", soft: "#D7F3EF" },
  { n: "02", icon: Mic, title: "Role-play — solo or with a peer", desc: "Consult a realistic voice AI patient by talking or typing, or pair up over live video with another candidate.", tint: "#0EA5E9", soft: "#D9F0FF" },
  { n: "03", icon: ClipboardCheck, title: "Get examiner-grade feedback", desc: "Receive a scored report across every AMC domain with strengths, critical misses and a model answer to compare.", tint: "#E11D48", soft: "#FFE4E6" },
];

const HIGHLIGHTS = [
  { icon: ClipboardCheck, title: "Recall-based cases", desc: "Our station library is modelled on the scenarios AMC candidates commonly report — across history, examination, diagnosis and counselling tasks." },
  { icon: Users, title: "Realistic exam format", desc: "Address the patient or ask the examiner for examination findings and investigations — just like a real station." },
  { icon: Camera, title: "Your own material", desc: "Turn your study notes or photos into private practice cases in seconds." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    api.plans().then((d) => { setPlans(d.plans || []); setPricing(d); }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="absolute inset-0 noise opacity-60" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24 lg:px-8">
          <div>
            <Badge variant="outline" className="mb-5 border-primary/30 bg-accent text-accent-foreground">
              <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Built for the AMC Clinical Exam
            </Badge>
            <h1 className="font-display text-4xl font-semibold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Practise with real peers or solo with AI &mdash;{" "}
              <span className="text-primary">and get detailed feedback.</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg">
              Run live video role-play with fellow AMC candidates, sharpen up with a realistic voice AI
              patient and examiner, and get scored feedback across every AMC domain — with 165+ ready cases.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button size="lg" data-testid="hero-get-started" onClick={() => navigate("/register")} className="text-base">
                Sign up now <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" data-testid="hero-view-pricing" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} className="text-base">
                View pricing
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2"><Mic className="h-4 w-4 text-primary" /> Voice AI patient &amp; examiner</span>
              <span className="inline-flex items-center gap-2"><Star className="h-4 w-4 text-primary" /> 1&ndash;7 AMC-domain feedback</span>
              <span className="inline-flex items-center gap-2"><BookOpen className="h-4 w-4 text-primary" /> 165+ cases</span>
              <span className="inline-flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Live peer video</span>
            </div>
          </div>

          {/* Product mock: detailed examiner report */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative">
            <div className="overflow-hidden rounded-2xl border border-border bg-card card-shadow">
              {/* Report header */}
              <div className="flex items-start gap-4 border-b border-border bg-primary/5 p-5">
                <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-primary text-primary-foreground">
                  <span className="font-display text-2xl font-semibold leading-none">6</span>
                  <span className="text-[9px] uppercase tracking-wide opacity-80">/ 7</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary"><ClipboardCheck className="h-4 w-4" /> Examiner report</span>
                    <Badge className="bg-[#DCFCE7] text-[#166534] border border-[#86EFAC] hover:bg-[#DCFCE7]">Clear pass</Badge>
                  </div>
                  <h3 className="mt-1 truncate font-display text-lg font-semibold leading-tight">Acute Chest Pain</h3>
                  <p className="text-xs text-muted-foreground">Medicine · 55yo male, central crushing chest pain in ED · pass mark 4</p>
                </div>
              </div>

              {/* Domain scores */}
              <div className="p-5">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Domain breakdown</p>
                <div className="space-y-2">
                  {[
                    { d: "Communication & rapport", s: 7 },
                    { d: "History taking", s: 6 },
                    { d: "Clinical reasoning", s: 6 },
                    { d: "Investigations & Dx", s: 6 },
                    { d: "Management & counselling", s: 5 },
                    { d: "Professionalism", s: 7 },
                  ].map((b) => (
                    <div key={b.d}>
                      <div className="flex items-center justify-between text-[11px] font-medium"><span>{b.d}</span><span className="text-primary">{b.s}/7</span></div>
                      <div className="mt-1 h-1.5 rounded-full bg-muted"><div className="h-1.5 rounded-full bg-primary" style={{ width: `${(b.s / 7) * 100}%` }} /></div>
                    </div>
                  ))}
                </div>

                {/* Examiner narrative */}
                <div className="mt-4 rounded-xl border-l-2 border-primary bg-primary/5 px-3 py-2.5">
                  <p className="text-[11px] font-semibold text-primary">Examiner's comment</p>
                  <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
                    &ldquo;A well-structured, empathetic consult. You reached the working diagnosis of ACS efficiently and
                    escalated appropriately. To move from a solid to a strong pass, tighten your closing with explicit
                    safety-netting and confirm the patient understood the plan.&rdquo;
                  </p>
                </div>

                {/* Strengths */}
                <p className="mt-4 text-[11px] font-semibold text-foreground">What you did well</p>
                <ul className="mt-1.5 space-y-1 text-[11px] leading-4 text-muted-foreground">
                  <li className="flex items-start gap-1.5"><Check className="mt-0.5 h-3 w-3 shrink-0 text-primary" /> Systematic SOCRATES pain history and screened cardiac risk factors</li>
                  <li className="flex items-start gap-1.5"><Check className="mt-0.5 h-3 w-3 shrink-0 text-primary" /> Ordered ECG + troponin promptly and justified the urgency</li>
                  <li className="flex items-start gap-1.5"><Check className="mt-0.5 h-3 w-3 shrink-0 text-primary" /> Explained the working diagnosis in plain, reassuring language</li>
                </ul>

                {/* Improvements */}
                <p className="mt-3 text-[11px] font-semibold text-foreground">Focus next on</p>
                <ul className="mt-1.5 space-y-1 text-[11px] leading-4 text-muted-foreground">
                  <li className="flex items-start gap-1.5"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D97706]" /> Safety-net clearly &mdash; advise to return if pain worsens or radiates to jaw/arm</li>
                  <li className="flex items-start gap-1.5"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D97706]" /> Use teach-back to confirm understanding before closing the consult</li>
                </ul>

                {/* Model answer chip */}
                <div className="mt-4 flex items-center justify-between rounded-xl border border-border bg-card px-3 py-2.5">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary"><BookOpen className="h-3.5 w-3.5" /> Model answer available</span>
                  <span className="text-[11px] font-medium text-muted-foreground">Reveal &amp; compare →</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Everything at a glance */}
      <section id="glance" className="relative overflow-hidden bg-gradient-to-br from-[#0F766E] to-[#0B564F] text-white">
        <div className="absolute inset-0 noise opacity-30" />
        <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-20 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-xs font-semibold tracking-wide backdrop-blur">
              <Sparkles className="h-3.5 w-3.5" /> Your complete AMC prep, in one place
            </span>
            <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Everything you need, at a glance</h2>
            <p className="mt-3 text-white/75">Six ways Role-play Connect gets you exam-ready.</p>
          </div>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {GLANCE.map((g, i) => (
              <motion.div
                key={g.title}
                data-testid={`glance-tile-${i}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.35, delay: i * 0.05 }}
                className="group rounded-2xl border border-white/15 bg-white/[0.08] p-6 backdrop-blur transition-colors duration-200 hover:bg-white/[0.14]"
              >
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-white/15 text-white ring-1 ring-white/20 transition-transform duration-200 group-hover:scale-110">
                  <g.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-4 font-display text-lg font-semibold">{g.title}</h3>
                <p className="mt-1.5 text-sm leading-6 text-white/75">{g.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 border-t border-white/15 pt-8 text-center">
            {STATS.map((s) => (
              <div key={s.label} data-testid={`glance-stat-${s.label}`} className="min-w-[120px]">
                <div className="font-display text-3xl font-semibold sm:text-4xl">{s.value}</div>
                <div className="mt-1 text-xs uppercase tracking-wider text-white/70">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Button size="lg" variant="secondary" data-testid="glance-cta" onClick={() => navigate("/register")} className="bg-white text-[#0F766E] hover:bg-white/90">
              Start practising free <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Failed a station? Feedback breakdown */}
      <section id="feedback" className="bg-sand">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24 lg:px-8">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold text-primary"><Star className="h-3.5 w-3.5" /> Examiner-grade feedback</span>
            <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Failed a station? That's exactly what practice is for.</h2>
            <p className="mt-4 text-muted-foreground">Every session gives you a full breakdown &mdash; what you did right, what you missed, and exactly where the critical errors were. No more guessing what went wrong.</p>
            <p className="mt-3 text-muted-foreground">Like the real exam, each station is scored only on the <strong className="text-foreground">domains it actually assesses</strong> (each rated 1&ndash;7), plus a <strong className="text-foreground">holistic global rating</strong> — the examiner's overall judgement of the station, not an average. You also get the specific points you'd be marked down for and a full <strong className="text-foreground">model answer</strong> to compare against &mdash; so next time, you already know what to fix.</p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {[
                { i: ClipboardCheck, t: "Only the domains that apply" },
                { i: Star, t: "Holistic global rating (1–7)" },
                { i: Check, t: "What you did well" },
                { i: BookOpen, t: "Full model answer" },
              ].map((f) => (
                <div key={f.t} className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5 text-sm font-medium">
                  <f.i className="h-4 w-4 text-primary" /> {f.t}
                </div>
              ))}
            </div>
            <Button size="lg" className="mt-8" data-testid="feedback-cta" onClick={() => navigate("/register")}>
              Start practising free <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.4 }}
            className="rounded-2xl border border-border bg-card p-6 card-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-display text-lg font-semibold">Ureteric Colic</h3>
                <p className="text-xs text-muted-foreground">Urology &middot; global rating 1&ndash;7 &middot; pass mark 4</p>
              </div>
              <Badge className="bg-[#FEE2E2] text-[#991B1B] border border-[#FCA5A5] hover:bg-[#FEE2E2]">2/7 &middot; Fail</Badge>
            </div>
            <div className="mt-5 space-y-3">
              {[
                { d: "History Taking", s: 3, c: "#D97706" },
                { d: "Clinical Reasoning", s: 2, c: "#DC2626" },
                { d: "Diagnostic Formulation", s: 1, c: "#DC2626" },
                { d: "Patient Safety", s: 2, c: "#DC2626" },
              ].map((b) => (
                <div key={b.d}>
                  <div className="flex items-center justify-between text-xs font-medium"><span>{b.d}</span><span style={{ color: b.c }}>{b.s}/7</span></div>
                  <div className="mt-1 h-2 rounded-full bg-muted"><div className="h-2 rounded-full" style={{ width: `${(b.s / 7) * 100}%`, background: b.c }} /></div>
                </div>
              ))}
            </div>
            <div className="mt-5 rounded-xl border border-[#FCA5A5] bg-[#FEF2F2] p-4">
              <p className="text-sm font-semibold text-[#991B1B]">Missed critical items</p>
              <ul className="mt-2 space-y-1.5 text-xs text-[#7F1D1D]">
                <li>&times; Haematuria &mdash; never asked</li>
                <li>&times; Red-flag fever / rigors screening omitted</li>
                <li>&times; No non-contrast CT KUB requested</li>
              </ul>
            </div>
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-border bg-primary/5 px-4 py-3 text-sm font-medium text-primary">
              <BookOpen className="h-4 w-4" /> Model answer &mdash; reveal &amp; compare
            </div>
          </motion.div>
        </div>
      </section>

      {/* Peer-to-peer role-play — flagship */}
      <section id="peer" className="bg-sand">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <Badge variant="outline" className="mb-4 border-primary/30 bg-accent text-accent-foreground">
                <Users className="mr-1.5 h-3.5 w-3.5" /> Practise with real candidates
              </Badge>
              <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Live peer-to-peer role-play</h2>
              <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-[#FBBF24]/50 bg-[#FFFBEB] p-3.5 text-sm text-[#92400E]" data-testid="landing-peer-paused">
                <Radio className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Live 1-to-1 practice is temporarily paused, will reopen soon as our community grows &mdash; stay tuned!</span>
              </div>
              <p className="mt-4 text-base leading-7 text-muted-foreground">
                Nothing prepares you like practising with another candidate. Pair up over live video to run stations,
                swap between the doctor and patient roles, and give each other real-time feedback — the closest thing to exam day.
              </p>
              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-primary" /> Face-to-face video sessions with fellow AMC candidates</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-primary" /> Get matched instantly during Rush Hours, or host your own room anytime</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-primary" /> Private rooms you control — approve who joins, up to 3 for study groups</li>
              </ul>
              <Button size="lg" className="mt-7 text-base" data-testid="peer-cta" onClick={() => navigate("/register")}>
                Join now &amp; get notified <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="rounded-2xl border border-border bg-card p-6 card-shadow">
                <span className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: "#FFE4E6", color: "#E11D48" }}><Users className="h-5 w-5" /></span>
                <h3 className="mt-4 text-lg font-semibold">Find a partner</h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">Join the matchmaking queue during Rush Hours and get paired with another candidate for a 1:1 video station.</p>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6 card-shadow">
                <span className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: "#D7F3EF", color: "#0F766E" }}><Video className="h-5 w-5" /></span>
                <h3 className="mt-4 text-lg font-semibold">Host a private room</h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">Create your own room anytime, and approve exactly who joins — up to 3 people. Perfect for your study group.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="how" className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-accent px-3 py-1 text-xs font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" /> Simple by design
          </span>
          <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight sm:text-4xl">How it works</h2>
          <p className="mt-3 text-muted-foreground">Three steps to sharper consultations &mdash; from case to exam-ready.</p>
        </div>
        <div className="relative mt-14 grid gap-6 md:grid-cols-3">
          {/* connector line (desktop) */}
          <div className="pointer-events-none absolute left-0 right-0 top-8 hidden h-px bg-gradient-to-r from-transparent via-border to-transparent md:block" />
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              data-testid={`how-step-${i}`}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.35, delay: i * 0.1 }}
              className="group relative rounded-2xl border border-border bg-card p-7 card-shadow transition-shadow hover:card-shadow-hover"
            >
              <span className="pointer-events-none absolute right-5 top-4 font-display text-6xl font-semibold leading-none text-primary/[0.06]">{s.n}</span>
              <span className="relative grid h-14 w-14 place-items-center rounded-2xl ring-4 ring-background transition-transform duration-200 group-hover:scale-105" style={{ background: s.soft, color: s.tint }}>
                <s.icon className="h-6 w-6" />
              </span>
              <div className="mt-5 flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-primary">Step {s.n}</span>
              </div>
              <h3 className="mt-1.5 font-display text-lg font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="bg-sand">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-card px-3 py-1 text-xs font-semibold text-primary">
              <ShieldCheck className="h-3.5 w-3.5" /> A complete toolkit
            </span>
            <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Everything you need to prepare</h2>
            <p className="mt-3 text-muted-foreground">Clinical-grade practice, feedback and case tools &mdash; all in one place.</p>
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                data-testid={`feature-card-${i}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 card-shadow transition-[box-shadow,transform] duration-200 hover:-translate-y-1 hover:card-shadow-hover"
              >
                <span className="absolute inset-x-0 top-0 h-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100" style={{ background: f.tint }} />
                <span className="grid h-12 w-12 place-items-center rounded-xl transition-transform duration-200 group-hover:scale-110" style={{ background: f.soft, color: f.tint }}>
                  <f.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-4 flex items-center gap-2 text-lg font-semibold">
                  {f.title}
                  {f.beta && <span className="rounded-full border border-[#FBBF24] bg-[#FFFBEB] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#92400E]">Beta</span>}
                </h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-semibold tracking-tight">Simple, candidate-friendly pricing</h2>
          <p className="mt-3 text-muted-foreground">Unlock unlimited AI practice and feedback.</p>
        </div>
        {pricing && (pricing.phase === "promo" || pricing.flash_discount_active) && (
          <div className="mx-auto mt-8 max-w-3xl"><PricingUrgencyBanner meta={pricing} /></div>
        )}
        <div className="mx-auto mt-12 grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((p) => (
            <div key={p.id} data-testid={`plan-card-${p.id}`} className={`relative rounded-2xl border bg-card p-7 card-shadow ${p.best_value ? "border-primary ring-1 ring-primary" : "border-border"}`}>
              {p.best_value && <Badge className="absolute -top-3 left-7 bg-primary text-primary-foreground hover:bg-primary">Best value</Badge>}
              <h3 className="text-lg font-semibold">{p.name}</h3>
              <div className="mt-3 flex items-end gap-2">
                <span className="font-display text-4xl font-semibold">{fmtMoney(p.amount)}</span>
                <span className="mb-1 text-sm text-muted-foreground">/ {p.months} mo</span>
              </div>
              <PlanPriceMeta plan={p} />
              <ul className="mt-6 space-y-3 text-sm">
                {["Unlimited AI role-play", "Examiner feedback reports", "All case library scenarios", "Create unlimited custom cases", "Live peer practice"].map((b) => (
                  <li key={b} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-primary" /> {b}</li>
                ))}
              </ul>
              <Button className="mt-7 w-full" size="lg" variant={p.best_value ? "default" : "outline"} data-testid={`plan-cta-${p.id}`} onClick={() => navigate("/register")}>
                Get started
              </Button>
            </div>
          ))}
          {plans.length === 0 && (
            <div className="sm:col-span-2 lg:col-span-3 rounded-2xl border border-border bg-card p-7 text-center text-muted-foreground card-shadow">
              <Stethoscope className="mx-auto h-8 w-8 text-primary" />
              <p className="mt-3">Create a free account to see plans and start practising.</p>
              <Button className="mt-4" onClick={() => navigate("/register")}>Create account</Button>
            </div>
          )}
        </div>

        {/* FAQ */}
        <div className="mx-auto mt-14 max-w-2xl">
          <h3 className="text-center font-display text-2xl font-semibold">Frequently asked</h3>
          <Accordion type="single" collapsible className="mt-6">
            <AccordionItem value="1"><AccordionTrigger>Is this affiliated with the AMC?</AccordionTrigger><AccordionContent>No. Role-play Connect is an independent educational aid designed to help candidates practise. It does not guarantee any exam outcome.</AccordionContent></AccordionItem>
            <AccordionItem value="2"><AccordionTrigger>Does it auto-renew? Can I cancel?</AccordionTrigger><AccordionContent>It's your choice at checkout. Pick <strong>auto-renewing</strong> and your access keeps going each period at your locked-in price until you cancel — or pick <strong>one-time</strong> for a single payment that never renews. Want to try first? Two full sample cases (a GIT and a Counselling station) are <strong>free</strong> — no card needed. You can turn off auto-renewal anytime from your Subscription page — you'll keep access until the period you've already paid for runs out, and your progress and saved cases are always kept.</AccordionContent></AccordionItem>
            <AccordionItem value="3"><AccordionTrigger>What are custom cases?</AccordionTrigger><AccordionContent>You can paste notes or upload photos of your study material and our AI turns them into private, practiseable role-play scenarios just for you.</AccordionContent></AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Why it works (honest, no fabricated reviews) */}
      <section className="overflow-hidden bg-sand">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <Badge variant="outline" className="mb-4 border-primary/30 bg-accent text-accent-foreground">Currently in beta</Badge>
            <h2 className="font-display text-3xl font-semibold tracking-tight">Built around the real exam</h2>
            <p className="mt-3 text-muted-foreground">
              We're a new, independent tool actively improving with candidate feedback. Here's what makes practice here feel like the real thing.
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {HIGHLIGHTS.map((h, i) => (
              <motion.div key={h.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.3, delay: i * 0.06 }}
                className="rounded-xl border border-border bg-card p-6 card-shadow">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-accent text-primary"><h.icon className="h-5 w-5" /></span>
                <h3 className="mt-4 text-lg font-semibold">{h.title}</h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{h.desc}</p>
              </motion.div>
            ))}
          </div>
          <p className="mx-auto mt-10 max-w-2xl rounded-xl border border-border bg-card p-4 text-center text-sm text-muted-foreground card-shadow">
            <span className="font-semibold text-foreground">A note on honesty:</span> the AI patient is still improving, so the most reliable part is your detailed examiner-style feedback at the end of each session.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-2xl bg-primary px-8 py-14 text-center text-primary-foreground">
          <div className="absolute inset-0 hero-glow opacity-30" />
          <div className="relative">
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Ready to practise like it's exam day?</h2>
            <p className="mx-auto mt-3 max-w-xl opacity-90">Join candidates sharpening their consultations with AI role-play and examiner-grade feedback.</p>
            <Button size="lg" variant="secondary" data-testid="cta-get-started" onClick={() => navigate("/register")} className="mt-7 text-base">
              Create your free account <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
